import { mergeDeep } from '@toggle/helpers';
import * as Types from 'echarts/types/dist/echarts';

import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { DesignToken, ThemeName } from '~/design-tokens/themes/themes';

import { mergeSeriesLabel, mergeYAxis } from './utils';

export interface ApacheChartBase {
  theme?: ThemeName;
  onInit?: (refDom: HTMLDivElement) => void;
  isModal?: boolean;
}

export const DEFAULT_CHART_HEIGHT = 450;
export const DEFAULT_CHART_WIDTH = 700;
export const LEGEND_FONT_SIZE = 14;
export const LEGEND_BUFFER_COUNT = 1;
export const Y_AXIS_LABEL_WIDTH = 80;
export const Y_AXIS_PADDING = 10;

const DEFAULT_BACKGROUND_COLOR = 'transparent';

const PAGE_ICON_PREV_HORIZONTAL =
  'path://M0.488091 6.86992C-0.162697 7.49466 -0.162697 8.50923 0.48809 9.13397L7.15215 15.5313C7.63113 15.9911 8.34439 16.1261 8.96915 15.8762C9.59391 15.6263 10 15.0465 10 14.3968L10 1.60212C10 0.957385 9.59391 0.372628 8.96915 0.122733C8.3444 -0.127163 7.63113 0.0127776 7.15215 0.467588L0.488091 6.86492V6.86992Z';
const PAGE_ICON_NEXT_HORIZONTAL =
  'path://M9.51191 6.86992C10.1627 7.49466 10.1627 8.50923 9.51191 9.13397L2.84785 15.5313C2.36887 15.9911 1.65561 16.1261 1.03085 15.8762C0.406094 15.6263 1.7099e-06 15.0465 1.62115e-06 14.3968L-1.44962e-07 1.60212C-2.33034e-07 0.957385 0.406092 0.372628 1.03085 0.122733C1.6556 -0.127163 2.36887 0.0127776 2.84785 0.467588L9.51191 6.86492V6.86992Z';
export interface SingleSeries {
  name: string;
  data: (number | null)[];
}

interface ApacheConfigArgs {
  theme: ThemeName;
  options: Types.EChartsOption;
  isModal?: boolean;
}

export const createApacheConfig = ({
  theme,
  options,
  isModal,
}: ApacheConfigArgs): Types.EChartsOption => {
  const defaultAxisConfig = {
    axisLabel: {
      fontSize: 12,
      fontFamily: 'Roboto Mono',
      overflow: 'break',
      width: Y_AXIS_LABEL_WIDTH,
    },
    axisTick: {
      lineStyle: {
        color: getThemeColor('--border-default', theme),
      },
    },
  };

  const defaultXAxisConfig = mergeDeep(defaultAxisConfig, {
    alignTicks: true,
    position: 'bottom',
    axisTick: {
      alignWithLabel: true,
    },
    axisLabel: {
      align: 'center',
    },
    splitLine: {
      show: false,
    },
    ...(isModal
      ? {}
      : {
          nameTruncate: {
            maxWidth: Y_AXIS_LABEL_WIDTH,
            ellipsis: '...',
          },
        }),
  });

  const defaultGridConfig = {
    top: '10%',
    bottom: '20%',
    left: '3%',
    right: '5%',
    containLabel: true,
  };

  const defaultTooltipConfig = {
    backgroundColor: getThemeColor('--surface-default', theme),
    borderColor: getThemeColor('--background-soft', theme),
    textStyle: {
      color: getThemeColor('--text-default', theme),
      fontSize: 12,
    },
    triggerOn: 'mousemove',
    padding: 12,
    extraCssText: 'box-shadow: var(--elevation-strong);',
  };

  const defaultLegendConfig = {
    type: 'scroll',
    pageIcons: {
      horizontal: [PAGE_ICON_PREV_HORIZONTAL, PAGE_ICON_NEXT_HORIZONTAL],
    },
    pageIconColor: getThemeColor('--action-primary-default', theme),
    pageIconInactiveColor: getThemeColor('--action-primary-disabled', theme),
    pageIconSize: [10, 14],
    pageTextStyle: {
      color: getThemeColor('--text-soft', theme),
      fontSize: LEGEND_FONT_SIZE,
    },
    bottom: options.title ? 20 : 15,
    right: 7,
    itemGap: 20,
    textStyle: {
      color: getThemeColor('--text-default', theme),
      fontSize: LEGEND_FONT_SIZE,
    },
    tooltip: {
      show: true,
    },
  };

  const seriesLabelConfig = {
    label: {
      color: getThemeColor('--text-soft', theme),
    },
    endLabel: {
      color: getThemeColor('--text-soft', theme),
    },
  };

  const xAxis = options.xAxis && mergeDeep(defaultXAxisConfig, options.xAxis);
  const yAxis =
    options.yAxis && mergeYAxis({ theme, customYAxis: options.yAxis, isModal });

  const baseGrid = isModal ? { containLabel: true } : defaultGridConfig;
  const grid = mergeDeep(baseGrid, options.grid ?? {});

  const axis = xAxis && yAxis ? { xAxis, yAxis } : {};
  const tooltip = mergeDeep(defaultTooltipConfig, options.tooltip ?? {});
  const legend = mergeDeep(defaultLegendConfig, options.legend ?? {});
  const processedSeries = mergeSeriesLabel({
    series: options.series,
    labelConfig: seriesLabelConfig,
  });

  return {
    ...options,
    backgroundColor: options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
    textStyle: {
      fontFamily: 'Nort',
    },
    ...axis,
    grid,
    tooltip,
    legend,
    series: processedSeries,
    color: Array.from({ length: 12 }, (_, i) =>
      getThemeColor(`--chart-multi-lines-hex-${i + 1}` as DesignToken, theme)
    ),
  };
};
